import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Lock, VpnKey, Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import i18next from "i18next";

const ItalyLogin: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { currentUser, login, loading, firebaseReady } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) {
      i18next.changeLanguage("it");
      navigate("/");
    }
  }, [currentUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Inserisci email e password");
      return;
    }

    if (!firebaseReady) {
      setError(
        "Firebase no está conectado en esta versión de la web. El usuario y la contraseña siguen en Firebase; hay que volver a poner las claves y publicar."
      );
      return;
    }

    setError("");
    setIsLoading(true);

    const success = await login(email, password);

    if (success) {
      i18next.changeLanguage("it");
      navigate("/");
    } else {
      setError("Credenziali non valide. Accesso negato.");
    }

    setIsLoading(false);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (currentUser) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(135deg, #009246 0%, #ffffff 50%, #ce2b37 100%)",
        }}
      >
        <Card sx={{ maxWidth: 400, textAlign: "center" }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom color="success.main">
              ✅ Accesso autorizzato!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Benvenuto in Italia! Reindirizzamento...
            </Typography>
            <CircularProgress sx={{ mt: 2 }} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #009246 0%, #ffffff 50%, #ce2b37 100%)",
        padding: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 450,
          width: "100%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ padding: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Lock sx={{ fontSize: 48, color: "#ce2b37", mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              🇮🇹 Accesso Italia
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Area riservata - Solo utenti autorizzati
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Inserisci le tue credenziali Firebase
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              type="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="outlined"
              sx={{ mb: 2 }}
              disabled={isLoading}
              autoComplete="email"
            />

            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              sx={{ mb: 2 }}
              disabled={isLoading}
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKey sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1.1rem",
                background: "linear-gradient(45deg, #009246 30%, #ce2b37 90%)",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #007a3d 30%, #b02530 90%)",
                },
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                  Accesso in corso...
                </>
              ) : (
                "Accedi con Firebase"
              )}
            </Button>
          </form>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              ¿Buscas datos de España?{" "}
              <Button
                variant="text"
                size="small"
                onClick={() => navigate("/")}
                sx={{
                  textTransform: "none",
                  color: "#AA151B",
                  fontWeight: "bold",
                }}
              >
                Acceso público aquí
              </Button>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ItalyLogin;
